define('presentation/shell/viewModels/appEntryShellViewModel',[
    'businessServices/events/eventManager',
    'businessServices/router/router',
    'businessServices/signup/signupSessionStore',
    'common/promises/promiseFactory',
    'constants/shellNameEnumerations',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/common/signupPageIndicatorWidget',
    'presentation/signup/viewModels/signupAsideViewModel',
    'presentation/signup/viewModels/signupForCustomerViewModel',
    'common/url/urlFormatter',
    'settings/navigationConfiguration'
], function() {
    return function() {
        let self = this;

        const SignupAsideViewModelConstructor = require('presentation/signup/viewModels/signupAsideViewModel');
        const SignupForCustomerViewModelConstructor = require('presentation/signup/viewModels/signupForCustomerViewModel');
        const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _shellNameEnumerations = require('constants/shellNameEnumerations');
        const _indicatorWidget = require('presentation/signup/common/signupPageIndicatorWidget');
        const _navigationConfiguration = require('settings/navigationConfiguration');

        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');
        const _urlFormatter = require('common/url/urlFormatter');

        // Routes that take the whole shell rather than sitting beside the aside.
        const FULL_BLEED_ROUTE_IDS = [
            _navigationConfiguration.routesById.signupFreeTrial.routeId,
            _navigationConfiguration.routesById.signupSuccess.routeId,
            _navigationConfiguration.routesById.signupScheduling.routeId,
        ];

        let _eventManager = null;
        let _signupSessionStore = null;
        let _signupNavigationManager = null;

        const _onShellUpdated = (shellName) => {
            if (shellName === _shellNameEnumerations.appEntry) {
                self.childViewModel(self.router.activeItem());
                self.shellIsActive(true);
                _refreshProgress();
                _refreshAsideVisibility();
                _refreshBackgroundWashVisibility();
                self.signupAsideViewModel.refresh();
            } else {
                self.childViewModel(null);
                self.shellIsActive(false);
            }
        };

        self.router = null;
        self.childViewModel = ko.observable();
        self.shellIsActive = ko.observable(true);
        self.isSignupForCustomer = ko.observable(false);
        self.signupForCustomerViewModel = null;
        self.signupAsideViewModel = null;

        self.isProgressBarVisible = ko.observable(false);
        self.progressPercentage = ko.observable(0);
        self.isAsideVisible = ko.observable(true);
        self.isBackgroundWashVisible = ko.observable(false);

        // The shell can activate before the router has an active instruction; skip until it does,
        // since _onShellUpdated refreshes all of these after every navigation.
        const _hasActiveRoute = () => !!self.router.activeInstruction();

        const _refreshProgress = () => {
            if (_hasActiveRoute() === false) {
                return;
            }
            _indicatorWidget.setIndicator(self.isProgressBarVisible, self.progressPercentage);
        };

        const _refreshAsideVisibility = () => {
            if (_hasActiveRoute() === false) {
                return;
            }
            const routeId = _signupNavigationManager.getRouteId();
            self.isAsideVisible(FULL_BLEED_ROUTE_IDS.indexOf(routeId) === -1);
        };

        const _refreshBackgroundWashVisibility = () => {
            if (_hasActiveRoute() === false) {
                return;
            }
            const routeId = _signupNavigationManager.getRouteId();
            self.isBackgroundWashVisible(routeId === _navigationConfiguration.routesById.signupSuccess.routeId);
        };

        // The shell never creates a signup session. It renders on free-trial, which sits outside the
        // flow, and a session created there is an orphan; the plans route guard creates the real one.
        const _seedProgress = () => {
            _signupSessionStore.getProgressPercentage(false)
                .done((progressPercentage) => {
                    self.progressPercentage(progressPercentage);
                    _refreshProgress();
                });
        };

        self.isModalVisible = ko.observable(false);
        self.isModalActive = ko.observable(false);
        self.homeUrl = _urlFormatter.buildFrontendUrl("/");

        // Leaving signup with a reserved number silently gives that number up, so warn first.
        self.handleLogoClick = () => {
            _isAPhoneNumberReserved()
                .done((isReserved) => {
                    if (isReserved) {
                        _displayModal();
                    } else {
                        self.navigateUserToHomePage();
                    }
                });
        };

        self.hideModal = () => {
            self.isModalVisible(false);
            setTimeout(() => {
                self.isModalActive(false);
            }, 250);
        };

        self.navigateUserToHomePage = () => {
            self.isModalVisible(false);
            self.isModalActive(false);
            self.router.navigate(self.homeUrl);
        };

        const _displayModal = () => {
            self.isModalActive(true);
            setTimeout(() => {
                self.isModalVisible(true);
            }, 100);
        };

        const _isAPhoneNumberReserved = () => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.get(false)
                    .fail(promise.reject)
                    .done((signupSession) => {
                        promise.resolve(signupSession !== null && signupSession.reservedPhoneNumber != null);
                    });
            });
        };

        self.activate = () => {
            self.router = require('businessServices/router/router');
            _eventManager = require('businessServices/events/eventManager');

            _signupSessionStore = new SignupSessionStoreConstructor();
            _signupSessionStore.init();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            self.childViewModel(self.router.activeItem());
            self.signupForCustomerViewModel = new SignupForCustomerViewModelConstructor();
            self.signupAsideViewModel = new SignupAsideViewModelConstructor();

            return _initialize();
        };

        const _initialize = () => {
            if (self.childViewModel() !== null) {
                self.childViewModel().parent = self;
            }

            const workSessionId = _workSessionIdStorage.getWorkSessionId();
            self.isSignupForCustomer(workSessionId !== null && workSessionId !== undefined);
            if (self.isSignupForCustomer()) {
                self.signupForCustomerViewModel.activate();
            }
            self.signupAsideViewModel.activate();

            _eventManager.subscribeShellUpdated(_onShellUpdated);

            _seedProgress();
            _refreshAsideVisibility();
            _refreshBackgroundWashVisibility();

            return _promiseFactory.wait();
        };
    };
});
